import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  Building2, Tv, ChevronRight, Loader2, Users, Film, Eye, AlertCircle,
  CheckCircle2, Clock, Plus, Mail, UserPlus, Trash2, Shield, Copy,
} from 'lucide-react';
import ProjectDeliverButton from '@/components/ProjectDeliverButton';

/**
 * Client Portfolio — admin view of every client organisation with the shows /
 * projects they own. Saves admins from flipping between project detail pages
 * to remember who owns what.
 *
 * Backed by GET /api/admin/clients/portfolio which returns rolled-up counts
 * (episodes, shots, completed shots, open submissions) per org.
 */
export default function ClientPortfolioPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const load = React.useCallback(() => {
    api.get('/admin/clients/portfolio')
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || 'Failed to load portfolio'));
  }, [api]);

  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <div className="p-6 bg-rose-500/10 border border-rose-500/30 rounded-md text-xs text-rose-300 flex items-center gap-2" data-testid="portfolio-error">
        <AlertCircle className="w-4 h-4" /> {error}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="p-12 text-center text-zinc-500">
        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
        <p className="text-xs">Loading client portfolio…</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="client-portfolio-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white flex items-center gap-2">
            <Building2 className="w-6 h-6 text-cyan-400" /> Client Portfolio
          </h1>
          <p className="text-xs text-zinc-400 mt-1">
            {data.count} client {data.count === 1 ? 'organisation' : 'organisations'} ·
            shows, episodes &amp; open work at a glance.
          </p>
        </div>
        <button onClick={() => navigate('/clients/new')} data-testid="portfolio-new-client-btn"
          className="h-9 px-4 bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-medium rounded-sm flex items-center gap-2">
          <Plus className="w-3.5 h-3.5" /> Onboard New Client
        </button>
      </div>

      {data.clients.length === 0 ? (
        <div className="p-12 bg-zinc-900 border border-dashed border-zinc-800 rounded-md text-center">
          <Building2 className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
          <p className="text-sm text-zinc-300">No clients yet</p>
          <p className="text-xs text-zinc-500 mt-1">
            Use the Onboard wizard to create your first client + project.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {data.clients.map(c => (
            <ClientCard key={c.org.id} client={c} navigate={navigate} api={api} onChanged={load} />
          ))}
        </div>
      )}
    </div>
  );
}

function ClientCard({ client, navigate, api, onChanged }) {
  const { org, projects, totals, primary_user } = client;
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState('');
  // Founder bug May 14, 2026: the user pressed the gold buttons looking
  // for "manage contacts" — instead the system fired welcome emails to
  // their clients. Now: (a) every button has a strong confirm, (b) a
  // dedicated "Manage Contacts" section lets them add/remove people
  // without triggering email.
  const [contactsOpen, setContactsOpen] = useState(false);
  const sendWelcome = async (mode) => {
    if (sending) return;
    const targetLabel = mode === 'primary' ? 'the primary contact only' : 'ALL client users';
    if (!window.confirm(
      `Send a welcome email to ${targetLabel} for "${org.client_name || org.name}"?\n\n` +
      `This will hit your Resend email quota.`
    )) return;
    setSending(true);
    setFeedback('');
    try {
      const r = await api.post(`/admin/clients/organizations/${org.id}/send-welcome`, { recipients: mode });
      setFeedback(`✅ Queued ${r.data.queued} email${r.data.queued === 1 ? '' : 's'}`);
    } catch (e) {
      setFeedback(e.response?.data?.detail || 'Failed to queue email');
    } finally {
      setSending(false);
      setTimeout(() => setFeedback(''), 4000);
    }
  };
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-md overflow-hidden"
         data-testid={`portfolio-org-${org.id}`}>
      <div className="p-4 border-b border-zinc-800 bg-gradient-to-r from-cyan-500/5 to-zinc-900">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-cyan-300" />
            </div>
            <div>
              <h3 className="text-sm font-heading font-semibold text-white">
                {org.client_name || org.company_name || org.name || '(unnamed)'}
              </h3>
              <p className="text-[11px] text-zinc-400">
                {org.country || org.country_of_incorporation || org.industry || '—'}
                {primary_user && (
                  <> · <span className="text-cyan-300">{primary_user.full_name}</span> ({primary_user.email})</>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {feedback && (
              <span className="text-[10px] text-emerald-300" data-testid={`welcome-feedback-${org.id}`}>{feedback}</span>
            )}
            <button
              onClick={() => setContactsOpen(o => !o)}
              data-testid={`manage-contacts-${org.id}`}
              className="h-7 px-2.5 text-[10px] text-cyan-200 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 rounded-sm flex items-center gap-1.5"
            >
              <Users className="w-3 h-3" />
              {contactsOpen ? 'Hide contacts' : `Manage contacts (${totals.contacts || 0})`}
            </button>
            <div className="inline-flex rounded-sm border border-amber-500/30 overflow-hidden"
                 title="Sends a welcome email — has a confirmation prompt to prevent accidental quota burn">
              <button onClick={() => sendWelcome('primary')} disabled={sending}
                data-testid={`send-welcome-primary-${org.id}`}
                className="px-2.5 py-1.5 text-[10px] text-amber-200 hover:bg-amber-500/15 disabled:opacity-50 flex items-center gap-1.5">
                {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
                Email Primary
              </button>
              <button onClick={() => sendWelcome('all')} disabled={sending}
                data-testid={`send-welcome-all-${org.id}`}
                className="px-2.5 py-1.5 text-[10px] text-amber-200 border-l border-amber-500/30 hover:bg-amber-500/15 disabled:opacity-50">
                Email All
              </button>
            </div>
          </div>
        </div>
        {contactsOpen && (
          <ContactsPanel orgId={org.id} api={api} />
        )}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
          <Stat label="Projects" value={totals.projects} color="text-cyan-300" />
          <Stat label="Episodes" value={totals.episodes} color="text-violet-300" />
          <Stat label="Shots" value={totals.shots} color="text-amber-300" />
          <Stat label="Completed" value={totals.completed_shots} color="text-emerald-300" />
          <Stat label="Open Reviews" value={totals.open_submissions} color="text-rose-300" />
          <Stat label="Contacts" value={totals.contacts} color="text-zinc-300" />
        </div>
      </div>

      {projects.length === 0 ? (
        <p className="p-4 text-xs text-zinc-500 italic">
          No projects linked yet — use Projects → Link to Client.
        </p>
      ) : (
        <div className="divide-y divide-zinc-800/40">
          {projects.map(p => (
            <ProjectRow key={p.id} project={p} navigate={navigate} onChanged={onChanged} />
          ))}
        </div>
      )}
    </div>
  );
}

function ProjectRow({ project, navigate, onChanged }) {
  const isShow = project.is_show;
  const delivered = project.status === 'delivered' || !!project.delivered_on;
  const progress = project.shot_count > 0
    ? Math.round((project.completed_shots / project.shot_count) * 100)
    : 0;
  return (
    <div className="px-4 py-3" data-testid={`portfolio-project-${project.id}`}>
      <div className="w-full flex items-center gap-3 group">
        <button onClick={() => navigate('/production')} className="flex-1 text-left flex items-center gap-3 min-w-0">
        {isShow ? <Tv className="w-4 h-4 text-violet-300" /> : <Film className="w-4 h-4 text-cyan-300" />}
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-zinc-100 group-hover:text-cyan-300 transition-colors">
              {project.name}
            </span>
            {isShow && (
              <span className="px-1.5 py-0.5 text-[9px] uppercase rounded bg-violet-500/20 text-violet-300">
                Show · {project.episode_count} ep
              </span>
            )}
            {!delivered && (
              <span className={`px-1.5 py-0.5 text-[9px] rounded status-${project.status || 'active'}`}>
                {project.status || 'active'}
              </span>
            )}
          </div>
          {!isShow && (
            <div className="flex items-center gap-2 mt-1.5">
              <div className="flex-1 max-w-[14rem] h-1 bg-zinc-800 rounded-full overflow-hidden">
                <div className={`h-full ${delivered ? 'bg-emerald-500' : 'bg-cyan-500'}`} style={{ width: `${delivered ? 100 : progress}%` }} />
              </div>
              <span className="text-[10px] font-mono text-zinc-500">
                {project.completed_shots}/{project.shot_count} ({progress}%)
              </span>
            </div>
          )}
        </div>
        </button>
        <ProjectDeliverButton project={project} onChanged={onChanged} />
        <ChevronRight className="w-4 h-4 text-zinc-700 group-hover:text-cyan-400" />
      </div>

      {/* Episode list nested under Shows */}
      {isShow && project.episodes?.length > 0 && (
        <div className="mt-2 ml-7 space-y-1">
          {project.episodes.map(ep => {
            const epProgress = ep.shot_count > 0
              ? Math.round((ep.completed_shots / ep.shot_count) * 100)
              : 0;
            return (
              <div key={ep.id} className="flex items-center gap-2 text-[11px] text-zinc-300 py-1 px-2 rounded hover:bg-zinc-800/40"
                   data-testid={`portfolio-episode-${ep.id}`}>
                <span className="font-mono text-zinc-500 w-12">EP{String(ep.episode_number || 0).padStart(2, '0')}</span>
                <span className="flex-1 truncate">{ep.name}</span>
                <div className="w-20 h-1 bg-zinc-800 rounded-full overflow-hidden">
                  <div className="h-full bg-violet-500" style={{ width: `${epProgress}%` }} />
                </div>
                <span className="text-[10px] font-mono text-zinc-500 w-14 text-right">
                  {ep.completed_shots || 0}/{ep.shot_count || 0}
                </span>
                {ep.delivered_on
                  ? <span className="text-[9px] text-emerald-300 whitespace-nowrap" data-testid={`episode-delivered-${ep.id}`}>Delivered</span>
                  : ep.status === 'completed' && <CheckCircle2 className="w-3 h-3 text-emerald-400" />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const Stat = ({ label, value, color }) => (
  <div className="bg-zinc-950 border border-zinc-800 rounded-sm px-2 py-1.5 text-center">
    <p className={`text-base font-heading font-bold ${color}`}>{value}</p>
    <p className="text-[9px] text-zinc-500 uppercase tracking-wider">{label}</p>
  </div>
);


// ─────────────────────────────────────────────────────────────────
// ContactsPanel — embedded inside ClientCard. Lists every contact +
// login user on the org, with an inline "Add Contact" form. Backed by
// the new endpoints /api/admin/clients/organizations/<org>/contacts.
// Founder ask (May 14, 2026): a way to add Simran@animaj.com as a
// backup approver to Animaj WITHOUT triggering a welcome email.
// ─────────────────────────────────────────────────────────────────
function ContactsPanel({ orgId, api }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [tempCred, setTempCred] = useState(null);
  const [form, setForm] = useState({
    name: '', email: '', role_in_project: 'Backup approver',
    title: '', phone: '', create_login: true,
  });

  const load = React.useCallback(() => {
    setErr('');
    api.get(`/admin/clients/organizations/${orgId}/contacts`)
      .then(r => setData(r.data))
      .catch(e => setErr(e.response?.data?.detail || 'Failed to load contacts'));
  }, [api, orgId]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return;
    setBusy(true); setErr(''); setTempCred(null);
    try {
      const r = await api.post(`/admin/clients/organizations/${orgId}/contacts`, form);
      if (r.data.temp_password) {
        setTempCred({
          email: form.email,
          password: r.data.temp_password,
          projects_granted: r.data.projects_granted || 0,
        });
      }
      setForm({ name: '', email: '', role_in_project: 'Backup approver',
                title: '', phone: '', create_login: true });
      setShowForm(false);
      load();
    } catch (e2) {
      setErr(e2.response?.data?.detail || 'Failed to add contact');
    } finally {
      setBusy(false);
    }
  };

  const removeContact = async (cid, label) => {
    if (!window.confirm(`Remove "${label}" from this client?\n\nNote: their login user (if any) will NOT be deleted — only the contact row.`)) return;
    try {
      await api.delete(`/admin/clients/organizations/${orgId}/contacts/${cid}`);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed');
    }
  };

  // Founder ask May 15, 2026: existing CC-only contact (Simran) needs
  // to be promoted to a full login. Calls the open-access endpoint
  // which auto-creates the user, generates a temp password, and grants
  // view access to every project under the org in one shot.
  const [openingId, setOpeningId] = useState(null);
  const openLogin = async (c) => {
    if (!window.confirm(`Create a client login for ${c.name} <${c.email}>?\n\nA temp password will be generated and shown to you below.`)) return;
    setOpeningId(c.id); setErr(''); setTempCred(null);
    try {
      const r = await api.post(`/admin/clients/contacts/${c.id}/open-access`, {});
      if (r.data.temp_password) {
        setTempCred({
          email: c.email,
          password: r.data.temp_password,
          projects_granted: r.data.projects_granted || 0,
        });
      } else if (r.data.already_had_login) {
        alert(`${c.email} already has a login. Use Admin → Users page to reset their password.`);
      }
      load();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to open access');
    } finally {
      setOpeningId(null);
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-zinc-800/60 space-y-3" data-testid={`contacts-panel-${orgId}`}>
      {err && (
        <div className="bg-rose-500/10 border border-rose-500/30 rounded-sm px-2 py-1.5 text-[11px] text-rose-300">
          {err}
        </div>
      )}
      {tempCred && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-md p-3 text-[11px] text-emerald-200 space-y-1"
             data-testid="contacts-temp-cred">
          <p className="font-semibold flex items-center gap-1.5">
            <Shield className="w-3.5 h-3.5" /> Login created for {tempCred.email}
          </p>
          <p className="font-mono text-emerald-100 flex items-center gap-2">
            Temp password: <span className="bg-zinc-950 px-2 py-0.5 rounded select-all">{tempCred.password}</span>
            <button onClick={() => navigator.clipboard.writeText(tempCred.password)} className="hover:text-white">
              <Copy className="w-3 h-3" />
            </button>
          </p>
          <p className="text-emerald-300/70 text-[10px]">
            Granted view access to {tempCred.projects_granted} project{tempCred.projects_granted === 1 ? '' : 's'}.
            Hand the password to the contact — they'll be forced to change it on first login.
          </p>
          <button onClick={() => setTempCred(null)} className="text-emerald-200/60 hover:text-emerald-100 text-[10px] underline">
            Dismiss
          </button>
        </div>
      )}
      {!data ? (
        <div className="text-zinc-500 text-[11px] flex items-center gap-2">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading contacts…
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-wider text-zinc-400">
              {data.contacts.length} contact{data.contacts.length === 1 ? '' : 's'} ·{' '}
              {data.users.length} login{data.users.length === 1 ? '' : 's'}
            </span>
            <button
              onClick={() => setShowForm(f => !f)}
              data-testid={`contacts-add-toggle-${orgId}`}
              className="h-7 px-2.5 text-[10px] bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-bold rounded-sm flex items-center gap-1.5"
            >
              <UserPlus className="w-3 h-3" /> {showForm ? 'Cancel' : 'Add contact'}
            </button>
          </div>

          {showForm && (
            <form onSubmit={submit} className="bg-zinc-950 border border-emerald-500/20 rounded-md p-3 grid grid-cols-2 gap-2"
                  data-testid={`contacts-add-form-${orgId}`}>
              <input placeholder="Full name *" value={form.name}
                     onChange={e => setForm({ ...form, name: e.target.value })}
                     data-testid="contact-form-name"
                     className="h-8 px-2 bg-zinc-900 border border-zinc-800 rounded text-xs text-zinc-100" required />
              <input type="email" placeholder="Email * (e.g. simran@animaj.com)" value={form.email}
                     onChange={e => setForm({ ...form, email: e.target.value })}
                     data-testid="contact-form-email"
                     className="h-8 px-2 bg-zinc-900 border border-zinc-800 rounded text-xs text-zinc-100" required />
              <input placeholder="Role (e.g. Backup approver)" value={form.role_in_project}
                     onChange={e => setForm({ ...form, role_in_project: e.target.value })}
                     className="h-8 px-2 bg-zinc-900 border border-zinc-800 rounded text-xs text-zinc-100" />
              <input placeholder="Title (optional)" value={form.title}
                     onChange={e => setForm({ ...form, title: e.target.value })}
                     className="h-8 px-2 bg-zinc-900 border border-zinc-800 rounded text-xs text-zinc-100" />
              <input placeholder="Phone (optional)" value={form.phone}
                     onChange={e => setForm({ ...form, phone: e.target.value })}
                     className="h-8 px-2 bg-zinc-900 border border-zinc-800 rounded text-xs text-zinc-100" />
              <label className="flex items-center gap-2 text-[11px] text-zinc-300 px-2">
                <input type="checkbox" checked={form.create_login}
                       onChange={e => setForm({ ...form, create_login: e.target.checked })}
                       data-testid="contact-form-create-login"
                       className="w-3.5 h-3.5 accent-emerald-500" />
                Create client login (temp password)
              </label>
              <div className="col-span-2 flex justify-end gap-2 mt-1">
                <button type="button" onClick={() => setShowForm(false)}
                        className="h-7 px-3 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-[11px] rounded">
                  Cancel
                </button>
                <button type="submit" disabled={busy}
                        data-testid={`contact-form-submit-${orgId}`}
                        className="h-7 px-3 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 text-[11px] font-bold rounded flex items-center gap-1.5 disabled:opacity-50">
                  {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserPlus className="w-3 h-3" />}
                  Add contact
                </button>
              </div>
            </form>
          )}

          {data.contacts.length > 0 && (
            <div className="space-y-1" data-testid={`contacts-list-${orgId}`}>
              {data.contacts.map(c => (
                <div key={c.id} className="flex items-center gap-3 px-2 py-1.5 bg-zinc-950/60 border border-zinc-800/60 rounded text-[11px]">
                  <Mail className="w-3 h-3 text-cyan-300 shrink-0" />
                  <span className="text-zinc-100 font-medium">{c.name}</span>
                  <span className="text-zinc-500 font-mono">{c.email}</span>
                  <span className="text-[10px] text-zinc-500 italic">{c.role_in_project || c.title || '—'}</span>
                  {c.has_login ? (
                    <span className="text-[9px] uppercase bg-emerald-500/15 text-emerald-300 px-1.5 py-0.5 rounded">login</span>
                  ) : (
                    <>
                      <span className="text-[9px] uppercase bg-zinc-700 text-zinc-300 px-1.5 py-0.5 rounded">CC-only</span>
                      <button
                        onClick={() => openLogin(c)}
                        disabled={openingId === c.id}
                        data-testid={`contact-open-login-${c.id}`}
                        title="Generate a client login + temp password for this contact"
                        className="text-[9px] uppercase font-bold bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-500/30 px-1.5 py-0.5 rounded flex items-center gap-1"
                      >
                        {openingId === c.id
                          ? <Loader2 className="w-2.5 h-2.5 animate-spin" />
                          : <Shield className="w-2.5 h-2.5" />}
                        Create login
                      </button>
                    </>
                  )}
                  <button onClick={() => removeContact(c.id, `${c.name} <${c.email}>`)}
                          className="ml-auto text-zinc-500 hover:text-rose-400" title="Remove contact">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Surface client login users that exist but aren't in client_contacts
              (e.g., legacy primary contact created by the onboarding wizard). */}
          {data.users.length > 0 && (
            <details className="text-[10px] text-zinc-500">
              <summary className="cursor-pointer hover:text-zinc-300">
                Show {data.users.length} client login user{data.users.length === 1 ? '' : 's'}
              </summary>
              <div className="mt-1 space-y-1">
                {data.users.map(u => (
                  <div key={u.id} className="flex items-center gap-3 px-2 py-1 bg-zinc-950/40 rounded">
                    <Shield className="w-3 h-3 text-emerald-300 shrink-0" />
                    <span className="text-zinc-200">{u.full_name}</span>
                    <span className="font-mono text-zinc-500">{u.email}</span>
                    <span className="text-[9px] uppercase text-zinc-500">{u.role.replace(/_/g, ' ')}</span>
                    {u.is_active === false && (
                      <span className="text-[9px] uppercase text-rose-300 bg-rose-500/15 px-1 rounded">disabled</span>
                    )}
                  </div>
                ))}
              </div>
            </details>
          )}
        </>
      )}
    </div>
  );
}
